import React, { useState, useEffect, useRef } from 'react';

/**
 * Voice Task Manager Component - FIXED VERSION
 * Fixed the re-render issue that was stopping voice recognition
 */

export function VoiceTaskManager({ onTaskCreate, internalPeople = [] }) {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [cleanedTranscript, setCleanedTranscript] = useState(''); // Cleaned version for display
    const [isProcessing, setIsProcessing] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false); // Yes/No confirmation dialog
    const [showModal, setShowModal] = useState(false);
    const [parsedTask, setParsedTask] = useState(null);
    const recognitionRef = useRef(null);
    const wakeWordTimeoutRef = useRef(null);
    const isListeningRef = useRef(false); // ✅ Use ref to track listening state

    // Function to clean up transcript - remove repeated words/phrases
    const cleanTranscript = (text) => {
        if (!text) return '';

        // Split into words
        const words = text.toLowerCase().split(/\s+/);
        const cleanedWords = [];

        for (let i = 0; i < words.length; i++) {
            const word = words[i];
            // Skip if this word is the same as the previous 1-3 words (handles stuttering)
            const isDuplicate =
                (cleanedWords.length > 0 && cleanedWords[cleanedWords.length - 1] === word) ||
                (cleanedWords.length > 1 && cleanedWords[cleanedWords.length - 2] === word && cleanedWords[cleanedWords.length - 1] === word);

            if (!isDuplicate) {
                cleanedWords.push(word);
            }
        }

        // Also remove repeated short phrases (2-3 word patterns)
        let result = cleanedWords.join(' ');

        // Remove patterns like "add of add of add of" -> "add of"
        result = result.replace(/(\b\w+\s+\w+\b)(?:\s+\1)+/gi, '$1');
        result = result.replace(/(\b\w+\b)(?:\s+\1)+/gi, '$1');

        // Clean up common speech recognition artifacts
        result = result.replace(/\s+/g, ' ').trim();

        // Capitalize first letter
        return result.charAt(0).toUpperCase() + result.slice(1);
    };

    useEffect(() => {
        console.log('🎤 VoiceTaskManager: Initializing...');
        
        // Check if browser supports Speech Recognition
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        
        if (!SpeechRecognition) {
            console.error('❌ Speech Recognition NOT supported in this browser');
            alert('Voice commands are not supported in your browser. Please use Chrome, Edge, or Safari.');
            return;
        }

        console.log('✅ Speech Recognition API available');

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        console.log('🎤 Speech Recognition configured:', {
            continuous: recognition.continuous,
            interimResults: recognition.interimResults,
            lang: recognition.lang
        });

        recognition.onstart = () => {
            console.log('🎤 Recognition STARTED');
        };

        recognition.onresult = (event) => {
            console.log('🎤 Recognition result received');
            
            let interimTranscript = '';
            let finalTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                console.log(`🎤 Result ${i}:`, {
                    transcript: transcript,
                    isFinal: event.results[i].isFinal,
                    confidence: event.results[i][0].confidence
                });
                
                if (event.results[i].isFinal) {
                    finalTranscript += transcript + ' ';
                } else {
                    interimTranscript += transcript;
                }
            }

            const fullTranscript = (finalTranscript + interimTranscript).trim();
            console.log('🎤 Full transcript:', fullTranscript);

            // Clean the transcript to remove duplicates and gibberish
            const cleaned = cleanTranscript(fullTranscript);
            console.log('🧹 Cleaned transcript:', cleaned);

            setTranscript(fullTranscript);
            setCleanedTranscript(cleaned);

            // If we have a final result with enough content, show confirmation dialog
            if (finalTranscript.trim().length > 0) {
                console.log('✅ Final result detected, showing confirmation dialog...');
                clearTimeout(wakeWordTimeoutRef.current);
                setIsProcessing(true);

                // Wait 1.5 seconds to see if more speech is coming, then show confirmation
                wakeWordTimeoutRef.current = setTimeout(() => {
                    console.log('⏰ Showing confirmation dialog for:', cleaned);
                    setIsProcessing(false);
                    setShowConfirmDialog(true); // Show Yes/No dialog instead of processing directly
                }, 1500);
            }
        };

        recognition.onerror = (event) => {
            console.error('❌ Speech recognition error:', {
                error: event.error,
                message: event.message
            });
            
            if (event.error === 'not-allowed') {
                alert('Microphone access denied. Please allow microphone access and try again.');
            } else if (event.error === 'no-speech') {
                console.log('⚠️ No speech detected');
            }
            
            setIsListening(false);
            isListeningRef.current = false;
        };

        recognition.onend = () => {
            console.log('🎤 Recognition ended. isListening:', isListeningRef.current);
            if (isListeningRef.current) {
                console.log('🔄 Restarting recognition (continuous mode)...');
                try {
                    recognition.start();
                } catch (error) {
                    console.error('❌ Error restarting recognition:', error);
                }
            }
        };

        recognitionRef.current = recognition;

        return () => {
            console.log('🧹 Cleaning up voice recognition');
            isListeningRef.current = false;
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.stop();
                } catch (error) {
                    // Ignore errors on cleanup
                }
            }
            clearTimeout(wakeWordTimeoutRef.current);
        };
    }, []); // ✅ Empty dependency array - only run once on mount

    const processVoiceCommand = (fullTranscript) => {
        console.log('🔄 Processing voice command:', fullTranscript);
        setIsProcessing(false);
        
        const command = fullTranscript.trim();
        console.log('📝 Command to parse:', command);
        
        if (command.length === 0) {
            console.log('⚠️ Command is empty');
            alert('No command detected. Please click the microphone and speak your command clearly.');
            return;
        }
        
        // Parse the voice command
        const taskData = parseVoiceCommand(command);
        console.log('✅ Parsed task data:', taskData);
        
        if (taskData && taskData.title) {
            setParsedTask(taskData);
            setShowModal(true);
            console.log('🎉 Modal should now be visible with task:', taskData.title);
        } else {
            console.log('❌ Failed to parse task data or no title extracted');
            alert('Could not understand the command. Try saying: "Add a task [description]" or "Create a task [description]"');
        }
    };

    const parseVoiceCommand = (command) => {
        console.log('🔍 Parsing command:', command);
        
        // Convert to lowercase for matching
        const lowerCommand = command.toLowerCase();
        
        // Default values
        const taskData = {
            title: '',
            taskOwner: [],
            priority: 'Medium',
            status: 'Pending',
            relatedTo: 'Others',
            date: getDefaultDate(),
            type: 'Activity',
            notes: ''
        };

        // Extract task title - look for common task creation phrases
        let titleMatch = lowerCommand.match(/(?:add|create|new|make)\s+(?:a\s+)?(?:task|activity)\s+(?:to\s+)?(?:of\s+)?(.+?)(?:\s+(?:and\s+)?assign|\s+for|\s+to\s+assign|$)/i);
        
        if (titleMatch && titleMatch[1]) {
            taskData.title = titleMatch[1].trim();
            console.log('📌 Title extracted:', taskData.title);
        } else {
            // If no task keyword found, use the entire command as title
            // But first check if it starts with common task actions
            if (lowerCommand.match(/^(add|create|new|make|task|activity)/i)) {
                // Remove the action word
                taskData.title = lowerCommand.replace(/^(add|create|new|make|task|activity)\s+/i, '').trim();
            } else {
                taskData.title = command.trim();
            }
            console.log('📌 Using command as title:', taskData.title);
        }

        // Extract assignees
        const assigneePatterns = [
            /assign\s+(?:it\s+)?to\s+(.+?)(?:\s+with|\s+priority|\s+by|\s+on|$)/i,
            /assign\s+(.+?)(?:\s+with|\s+priority|\s+by|\s+on|$)/i,
            /for\s+(.+?)(?:\s+with|\s+priority|\s+by|\s+on|$)/i,
        ];

        for (const pattern of assigneePatterns) {
            const assignMatch = command.match(pattern);
            if (assignMatch && assignMatch[1]) {
                const assignees = assignMatch[1].trim();
                taskData.taskOwner = parseAssignees(assignees, internalPeople);
                console.log('👥 Assignees extracted:', taskData.taskOwner);
                break;
            }
        }

        // Extract priority
        if (command.includes('high priority') || command.includes('urgent') || command.includes('critical')) {
            taskData.priority = 'High';
            console.log('⚡ Priority: High');
        } else if (command.includes('low priority') || command.includes('whenever')) {
            taskData.priority = 'Low';
            console.log('🔽 Priority: Low');
        }

        // Extract date
        const datePatterns = [
            { pattern: /tomorrow/i, days: 1 },
            { pattern: /today/i, days: 0 },
            { pattern: /next week/i, days: 7 },
            { pattern: /in (\d+) days?/i, extract: true },
        ];

        for (const { pattern, days, extract } of datePatterns) {
            const dateMatch = command.match(pattern);
            if (dateMatch) {
                const daysToAdd = extract ? parseInt(dateMatch[1]) : days;
                taskData.date = getDateFromNow(daysToAdd);
                console.log('📅 Date extracted:', taskData.date, `(${daysToAdd} days from now)`);
                break;
            }
        }

        // Extract category/type
        if (command.includes('funding')) {
            taskData.type = 'Funding';
        } else if (command.includes('follow up') || command.includes('followup')) {
            taskData.type = 'Follow-up';
        } else if (command.includes('email')) {
            taskData.type = 'Write-email';
        } else if (command.includes('meeting')) {
            taskData.type = 'Meeting';
        } else if (command.includes('competition')) {
            taskData.type = 'Competition';
        }
        console.log('📂 Type:', taskData.type);

        console.log('✅ Final parsed task:', taskData);
        return taskData;
    };

    const parseAssignees = (assigneeString, people) => {
        const names = assigneeString
            .split(/\s+and\s+|,\s*/)
            .map(name => name.trim())
            .filter(name => name.length > 0);

        console.log('👥 Parsing assignees:', names);

        const matchedNames = names.map(spokenName => {
            const lowerSpoken = spokenName.toLowerCase();
            
            // Exact match
            const exactMatch = people.find(p => 
                p.Name && p.Name.toLowerCase() === lowerSpoken
            );
            if (exactMatch) {
                console.log(`✅ Exact match: ${exactMatch.Name}`);
                return exactMatch.Name;
            }

            // First name match
            const firstNameMatch = people.find(p => 
                p.Name && p.Name.toLowerCase().split(' ')[0] === lowerSpoken
            );
            if (firstNameMatch) {
                console.log(`✅ First name match: ${firstNameMatch.Name}`);
                return firstNameMatch.Name;
            }

            // Partial match
            const partialMatch = people.find(p => 
                p.Name && p.Name.toLowerCase().includes(lowerSpoken)
            );
            if (partialMatch) {
                console.log(`✅ Partial match: ${partialMatch.Name}`);
                return partialMatch.Name;
            }

            console.log(`⚠️ No match for: ${spokenName}`);
            return spokenName;
        });

        return matchedNames;
    };

    const getDefaultDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        return date.toISOString().split('T')[0];
    };

    const getDateFromNow = (days) => {
        const date = new Date();
        date.setDate(date.getDate() + days);
        return date.toISOString().split('T')[0];
    };

    const startListening = () => {
        console.log('🎤 startListening called');
        if (recognitionRef.current && !isListeningRef.current) {
            try {
                console.log('🎤 Starting recognition...');
                recognitionRef.current.start();
                setIsListening(true);
                isListeningRef.current = true; // ✅ Set ref
                setTranscript('');
                console.log('✅ Recognition started successfully');
            } catch (error) {
                console.error('❌ Error starting recognition:', error);
                alert(`Failed to start voice recognition: ${error.message}`);
            }
        } else {
            console.log('⚠️ Cannot start - already listening or no recognition available');
        }
    };

    const stopListening = () => {
        console.log('🛑 stopListening called');
        if (recognitionRef.current && isListeningRef.current) {
            isListeningRef.current = false; // ✅ Set ref first
            try {
                recognitionRef.current.stop();
            } catch (error) {
                console.error('Error stopping recognition:', error);
            }
            setIsListening(false);
            setIsProcessing(false);
            clearTimeout(wakeWordTimeoutRef.current);
            console.log('✅ Recognition stopped');
        }
    };

    // Reset all voice state (used after task creation or cancel)
    const resetVoiceState = () => {
        setTranscript('');
        setCleanedTranscript('');
        setShowConfirmDialog(false);
        setIsProcessing(false);
    };

    const handleConfirmTask = async () => {
        console.log('💾 Confirming task creation:', parsedTask);
        if (parsedTask && onTaskCreate) {
            try {
                await onTaskCreate(parsedTask);
                setShowModal(false);
                setParsedTask(null);
                resetVoiceState(); // Clear all voice state
                console.log('✅ Task created successfully');
            } catch (error) {
                console.error('❌ Error creating task:', error);
                alert('Failed to create task: ' + error.message);
            }
        }
    };

    const handleEditTask = (field, value) => {
        setParsedTask(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle "No" - continue listening for more input
    const handleContinueListening = () => {
        console.log('🔄 User wants to continue speaking...');
        setShowConfirmDialog(false);
        // Don't clear transcript - keep accumulating
        // Recognition should still be running, just continue
        if (!isListeningRef.current && recognitionRef.current) {
            try {
                recognitionRef.current.start();
                setIsListening(true);
                isListeningRef.current = true;
            } catch (error) {
                console.error('Error restarting recognition:', error);
            }
        }
    };

    // Handle "Yes" - proceed to parse and show task form
    const handleProceedToTask = () => {
        console.log('✅ User confirmed, proceeding to task form...');
        setShowConfirmDialog(false);
        stopListening(); // Stop recording
        processVoiceCommand(cleanedTranscript || transcript); // Process the cleaned transcript
    };

    return (
        <div>
            {/* Voice Button */}
            <button
                onClick={isListening ? stopListening : startListening}
                style={{
                    background: isListening ? '#ef4444' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '48px',
                    height: '48px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transition: 'all 0.3s ease',
                    position: 'relative'
                }}
                title={isListening ? 'Stop listening' : 'Click and speak your command'}
            >
                {isListening ? '⏹️' : '🎤'}
                {isListening && (
                    <span style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        width: '16px',
                        height: '16px',
                        background: '#22c55e',
                        borderRadius: '50%',
                        animation: 'pulse 1.5s ease-in-out infinite'
                    }} />
                )}
            </button>

            {/* Listening Indicator */}
            {isListening && !showConfirmDialog && (
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0, 0, 0, 0.85)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '24px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    zIndex: 9999,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
                }}>
                    <span style={{ fontSize: '20px' }}>🎤</span>
                    <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {isProcessing ? '🔄 Processing your command...' : '🎤 Listening... Speak now'}
                        </div>
                        {(cleanedTranscript || transcript) && (
                            <div style={{ fontSize: '12px', opacity: 0.8, maxWidth: '400px' }}>
                                Heard: {(cleanedTranscript || transcript).substring(0, 100)}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Yes/No Confirmation Dialog */}
            {showConfirmDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '32px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎤</div>
                        <h2 style={{ marginBottom: '16px', color: '#1f2937', fontSize: '20px' }}>
                            Have you provided all the information for your task?
                        </h2>

                        <div style={{
                            background: '#f3f4f6',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '24px',
                            textAlign: 'left'
                        }}>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>
                                Heard:
                            </div>
                            <div style={{ fontSize: '14px', color: '#1f2937', lineHeight: '1.5' }}>
                                {cleanedTranscript || transcript || 'No speech detected'}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button
                                onClick={handleContinueListening}
                                style={{
                                    padding: '12px 24px',
                                    background: '#e5e7eb',
                                    color: '#374151',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <span>🎤</span> No, Continue Speaking
                            </button>
                            <button
                                onClick={handleProceedToTask}
                                style={{
                                    padding: '12px 24px',
                                    background: '#667eea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <span>✓</span> Yes, Proceed
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Task Form Modal */}
            {showModal && parsedTask && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }} onClick={() => {
                    setShowModal(false);
                    setParsedTask(null);
                    resetVoiceState();
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '24px',
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                    }} onClick={(e) => e.stopPropagation()}>
                        <h2 style={{ marginBottom: '20px', color: '#1f2937' }}>
                            🎤 Confirm Voice Task
                        </h2>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                Task Title *
                            </label>
                            <input
                                type="text"
                                value={parsedTask.title}
                                onChange={(e) => handleEditTask('title', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px',
                                    fontSize: '14px'
                                }}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Priority
                                </label>
                                <select
                                    value={parsedTask.priority}
                                    onChange={(e) => handleEditTask('priority', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Due Date
                                </label>
                                <input
                                    type="date"
                                    value={parsedTask.date}
                                    onChange={(e) => handleEditTask('date', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                Assigned To
                            </label>
                            <div style={{ 
                                fontSize: '14px', 
                                color: '#6b7280',
                                padding: '10px',
                                background: '#f9fafb',
                                borderRadius: '8px',
                                minHeight: '40px'
                            }}>
                                {parsedTask.taskOwner.length > 0 
                                    ? parsedTask.taskOwner.join(', ')
                                    : 'No assignees (you can add them after creation)'}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Category
                                </label>
                                <select
                                    value={parsedTask.type}
                                    onChange={(e) => handleEditTask('type', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="Funding">Funding</option>
                                    <option value="Follow-up">Follow-up</option>
                                    <option value="Write-email">Write-email</option>
                                    <option value="Meeting">Meeting</option>
                                    <option value="Competition">Competition</option>
                                    <option value="Activity">Activity</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Status
                                </label>
                                <select
                                    value={parsedTask.status}
                                    onChange={(e) => handleEditTask('status', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="Pending">Pending</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Completed">Completed</option>
                                    <option value="On Hold">On Hold</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                Related To
                            </label>
                            <input
                                type="text"
                                value={parsedTask.relatedTo}
                                onChange={(e) => handleEditTask('relatedTo', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px',
                                    fontSize: '14px'
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setParsedTask(null);
                                    resetVoiceState(); // Clear all voice state
                                }}
                                style={{
                                    padding: '10px 20px',
                                    background: '#e5e7eb',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmTask}
                                style={{
                                    padding: '10px 20px',
                                    background: '#667eea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                Create Task
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(1.2); }
                }
            `}</style>
        </div>
    );
}

export default VoiceTaskManager;
